import * as React from 'react';

import { FormattedMessage, useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import BackButton from 'src/features/navigation/components/BackButton';
import { GeneralBlock } from 'src/registry/components/blocks/GeneralBlock';
import GeneralBlockAttribute from 'src/registry/components/blocks/GeneralBlockAttribute';
import { Separator } from 'src/registry/ui/Separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'src/registry/ui/Tabs';
import { generalMessages } from 'src/registry/utils/messages';
import { useTitleStore } from 'src/stores/useTitleStore';
import { useGetAllowList } from 'src/features/allowList/api/useGetAllowList';
import { allowListMessages } from 'src/features/allowList/utils/allowListMessages';
import AllowListItems from 'src/features/allowListItem/AllowListItems';
import { useListZones } from 'src/features/zone/api/useListZones';
import { useListGateways } from 'src/features/gateway/api/useListGateways';
import { ALLOW_LIST_TYPE, ALLOW_LIST_TYPE_TRANSLATION_MAP } from 'src/features/allowList/utils/types';
import { useParking } from 'src/providers/ParkingProvider';
import { PERMISSION } from 'src/features/auth';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { SectionBlock, SectionBlockTitle } from 'src/registry/components/blocks/SectionBlock';
import UpdateAllowListFormModal from 'src/features/allowList/components/forms/UpdateAllowListFormModal';
import { Button } from 'src/registry/ui/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ICON_MAP } from 'src/utils/constants';
import { commonMessages } from 'src/utils/messages';
import DeleteAllowListFormModal from 'src/features/allowList/components/forms/DeleteAllowListFormModal';

export default function AllowListDashboard() {
    const { formatMessage, locale } = useIntl();
    const { listId = '' } = useParams();
    const { parkingId } = useParking();
    const manageAllowLists = useHasPermissions([PERMISSION.MANAGE_ALLOW_LISTS]);

    const { data: listData } = useGetAllowList({ listId, parkingId });
    const { data: zonesData } = useListZones({ parkingId });
    const { data: gatewaysData } = useListGateways({ parkingId });
    const { setTitle, removeTitle } = useTitleStore();

    React.useEffect(() => {
        setTitle(formatMessage(allowListMessages.listDashboard));
        return removeTitle;
    }, [formatMessage, removeTitle, setTitle]);

    const { zones, gateways } = React.useMemo(() => {
        const zones = listData?.zones
            .map(
                (value) =>
                    zonesData?.results
                        .find((option) => option.id === value)
                        ?.translations.find((translation) => translation.language === locale)?.name,
            )
            .join(',');

        const gateways = listData?.allowed_gateways
            .map(
                (value) =>
                    gatewaysData?.results
                        .find((option) => option.id === value)
                        ?.translations.find((translation) => translation.language === locale)?.name,
            )
            .join(',');

        return {
            zones,
            gateways,
        };
    }, [listData, zonesData, gatewaysData]);

    const allowListType =
        listData?.type === ALLOW_LIST_TYPE.WHITE ? allowListMessages.whiteList : allowListMessages.blackList;

    return (
        <>
            <BackButton />
            <SectionBlock>
                <div className="flex flex-row items-end justify-between p-0">
                    <span className="flex flex-row items-center">
                        <SectionBlockTitle className="mr-2">{listData?.name}</SectionBlockTitle>
                    </span>

                    {manageAllowLists && listData && (
                        <div className="flex flex-row gap-2">
                            <UpdateAllowListFormModal
                                selectedAllowList={listData}
                                semantics={{ entryName: formatMessage(allowListType).toLowerCase() }}
                            >
                                <Button size="mdIcon" variant="outline">
                                    <FontAwesomeIcon icon={ICON_MAP.EDIT} />
                                    <span className="hidden md:block">{formatMessage(commonMessages.edit)}</span>
                                </Button>
                            </UpdateAllowListFormModal>
                            <DeleteAllowListFormModal
                                selectedAllowList={listData}
                                semantics={{ entryName: formatMessage(allowListType).toLowerCase() }}
                            >
                                <Button size="mdIcon" variant="destructiveLight">
                                    <FontAwesomeIcon icon={ICON_MAP.DELETE} />
                                    <span className="hidden md:block">{formatMessage(generalMessages.delete)}</span>
                                </Button>
                            </DeleteAllowListFormModal>
                        </div>
                    )}
                </div>
                <GeneralBlock>
                    <div className="grid flex-3 grid-cols-3 gap-4">
                        <GeneralBlockAttribute label={formatMessage(generalMessages.name)} value={listData?.name} />
                        <GeneralBlockAttribute
                            label={`${formatMessage(generalMessages.description)}`}
                            value={listData?.description || '-'}
                        />
                        <GeneralBlockAttribute
                            label={`${formatMessage(allowListMessages.type)}`}
                            value={
                                listData?.type ? formatMessage(ALLOW_LIST_TYPE_TRANSLATION_MAP[listData?.type]) : '-'
                            }
                        />
                    </div>
                    <Separator />
                    <div className="grid flex-3 grid-cols-3 gap-4">
                        <GeneralBlockAttribute label={formatMessage(allowListMessages.zones)} value={zones || '-'} />
                        <GeneralBlockAttribute
                            label={formatMessage(allowListMessages.gateways)}
                            value={gateways || '-'}
                        />
                    </div>
                </GeneralBlock>
            </SectionBlock>
            <Tabs defaultValue="allow-list-items">
                <TabsList>
                    <TabsTrigger value="allow-list-items">
                        <FormattedMessage
                            defaultMessage={allowListMessages.allowListItems.defaultMessage}
                            id={allowListMessages.allowListItems.id}
                        />
                    </TabsTrigger>
                </TabsList>
                <TabsContent value="allow-list-items">
                    <AllowListItems listId={listId} />
                </TabsContent>
            </Tabs>
        </>
    );
}
